// @owners { team: patients }
import { H1 } from '@alto/design-system';
import { useNavigation } from '@alto/navigation';
import { format, parse } from 'date-fns';
import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
// eslint-disable-next-line import/no-deprecated
import { validateUser } from '~shared/actions/auth';
// eslint-disable-next-line import/no-deprecated
import { fetchOnboardState } from '~shared/actions/onboarding';
import {
  ONBOARD_STATE_DONE,
  ONBOARD_STATE_TO_ROUTE,
  VERIFY,
} from '~shared/features/onboarding/helpers/onboardingRouteHandler';
import { DATE_FORMATS } from '~shared/helpers/date';
import { useAnalytics } from '~shared/hooks';
import { EVENTS } from '~shared/lib/analytics/src/constants';
import { useDispatchShared } from '~shared/store';
import { type Values as VerificationDetails, Verify } from './Verify';
import { PageBackground, SingleColumnContentWrapper } from '~web/components/Page';
import { nextUrl } from '~web/features/onboarding/helpers/urlHandler';

export const OnboardingVerifyContainer = () => {
  const { tempPassword = '' } = useParams<{ tempPassword: string }>();
  const { navigate } = useNavigation();
  const [onboardState, setOnboardState] = useState('');
  const [disabled, setDisabled] = useState(false);
  const dispatch = useDispatchShared();
  const { trackPageView } = useAnalytics();

  useEffect(() => {
    const navigateIfOnboardingDone = async () => {
      // eslint-disable-next-line import/no-deprecated
      const result: { onboard_state?: string } = await dispatch(fetchOnboardState(tempPassword));
      const onboardState = result.onboard_state;
      setOnboardState(onboardState ?? '');

      if (onboardState === ONBOARD_STATE_DONE) {
        navigate('RouteLogin');
      }
    };
    navigateIfOnboardingDone();
  }, [dispatch, navigate, tempPassword]);

  useEffect(() => {
    trackPageView({
      event: EVENTS.ONBOARDING__VERIFY_ACCOUNT_VIEWED,
      params: {
        flow: 'onboard',
      },
      additionalFields: {
        userIdentifier: tempPassword,
        userIdentifierType: 'temp_password',
      },
    });
  }, [tempPassword, trackPageView]);

  const onVerify = async (data: VerificationDetails) => {
    setDisabled(true);
    const dob = format(
      parse(data.date_of_birth, DATE_FORMATS.DATE_INPUT_FORMAT, 0),
      DATE_FORMATS.YEAR_MONTH_DAY_DASHED,
    );
    try {
      const success = await dispatch(
        // eslint-disable-next-line import/no-deprecated
        validateUser({
          date_of_birth: dob,
          token: tempPassword,
          terms: data.terms,
        }),
      );

      if (!success) {
        return;
      }

      if (onboardState) {
        const desiredRoute = ONBOARD_STATE_TO_ROUTE[onboardState];
        const nextRoute = nextUrl({
          currentRoute: VERIFY,
          desiredRoute,
        });

        navigate(nextRoute);
      } else {
        navigate(nextUrl({ currentRoute: VERIFY }));
      }
    } finally {
      setDisabled(false);
    }
  };

  return (
    <PageBackground white>
      <SingleColumnContentWrapper>
        <H1 center>Let’s verify your identity.</H1>
        <Verify
          tempPassword={tempPassword}
          onVerify={onVerify}
          disabled={disabled}
        />
      </SingleColumnContentWrapper>
    </PageBackground>
  );
};
